import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users,
  UserPlus,
  Trash2,
  Edit,
  Search,
  ArrowRight,
  AlertTriangle,
  X,
  Phone,
  Mail,
  Loader,
} from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeesManagement() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, employee: null });
  const [deleting, setDeleting] = useState(false);

  const fetchEmployees = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/employees?include_admins=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    
    if (userData.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    
    fetchEmployees();
  }, [navigate, fetchEmployees]);

  const handleDelete = async () => {
    if (!deleteModal.employee) return;
    
    setDeleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/delete-user/${deleteModal.employee.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEmployees(employees.filter(emp => emp.id !== deleteModal.employee.id));
      setDeleteModal({ show: false, employee: null });
    } catch (error) {
      console.error('Error deleting employee:', error);
      alert(error.response?.data?.detail || 'حدث خطأ أثناء حذف الموظف');
    }
    setDeleting(false);
  };

  const getRoleBadge = (role) => {
    const roles = {
      admin: { label: 'مدير', color: 'bg-red-100 text-red-700' },
      lawyer: { label: 'محامي', color: 'bg-blue-100 text-blue-700' },
      accountant: { label: 'محاسب', color: 'bg-green-100 text-green-700' },
      staff: { label: 'موظف', color: 'bg-gray-100 text-gray-700' },
      marketer: { label: 'مسوق', color: 'bg-purple-100 text-purple-700' },
      client: { label: 'عميل', color: 'bg-yellow-100 text-yellow-700' },
    };
    const roleInfo = roles[role] || { label: role, color: 'bg-gray-100 text-gray-700' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${roleInfo.color}`}>
        {roleInfo.label}
      </span>
    );
  };

  const filteredEmployees = employees.filter(emp =>
    emp.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.phone?.includes(searchTerm)
  );

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      {/* Header */}
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">إدارة الموظفين</h1>
          </div>
          <button
            onClick={() => navigate('/create-user')}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-all font-body"
          >
            <UserPlus className="w-5 h-5" />
            إضافة موظف
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search & Stats */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="البحث بالاسم أو البريد أو الجوال..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-12 pl-4 py-3 text-lg border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold focus:border-gold transition-all font-body"
            />
          </div>
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
            <Users className="w-5 h-5 text-primary" />
            <span className="text-lg font-body">إجمالي: <strong>{employees.length}</strong> موظف</span>
          </div>
        </div>

        {/* Employees List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-gold" />
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-xl font-body">لا يوجد موظفين</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-right text-base font-semibold text-gray-600">الموظف</th>
                    <th className="px-6 py-4 text-center text-base font-semibold text-gray-600">الدور</th>
                    <th className="px-6 py-4 text-center text-base font-semibold text-gray-600">البريد الإلكتروني</th>
                    <th className="px-6 py-4 text-center text-base font-semibold text-gray-600">الجوال</th>
                    <th className="px-6 py-4 text-center text-base font-semibold text-gray-600">الإجراءات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEmployees.map((emp) => (
                    <tr key={emp.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white text-lg font-bold">
                            {emp.full_name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-body font-semibold text-lg text-gray-900">{emp.full_name}</p>
                            <p className="text-sm text-gray-500">{emp.department || 'غير محدد'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getRoleBadge(emp.role)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4" />
                          <span className="text-base">{emp.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span className="text-base">{emp.phone || '-'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/edit-user/${emp.id}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="تعديل"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          {emp.role !== 'admin' && (
                            <button
                              onClick={() => setDeleteModal({ show: true, employee: emp })}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="حذف"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-heading font-bold text-red-600">تأكيد الحذف</h3>
              <button
                onClick={() => setDeleteModal({ show: false, employee: null })}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-4 p-4 bg-red-50 rounded-xl mb-4">
              <AlertTriangle className="w-12 h-12 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-body text-lg text-gray-700">
                  هل أنت متأكد من حذف الموظف:
                </p>
                <p className="font-bold text-xl text-red-600 mt-1">
                  {deleteModal.employee?.full_name}
                </p>
              </div>
            </div>
            
            <p className="text-gray-500 text-base mb-6 font-body">
              سيتم حذف جميع بيانات هذا الموظف نهائياً ولا يمكن استرجاعها.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, employee: null })}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-body text-lg"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-body text-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    جاري الحذف...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    حذف نهائياً
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
